import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Invoice, UserRole, User } from '../types';
import { updateInvoice, hasMixedServiceTypes, splitInvoice } from '../services/firebaseService';
import './InvoiceActions.css';

interface InvoiceActionsProps {
  invoice: Invoice;
  userRole: UserRole;
  selectedTeam: string;
  onEdit: () => void;
  onDelete: () => void;
  onViewPDF: () => void;
  onRefresh: () => void;
  userData?: User;
}

const InvoiceActions: React.FC<InvoiceActionsProps> = ({
  invoice,
  userRole,
  selectedTeam,
  onEdit,
  onDelete,
  onViewPDF,
  onRefresh,
  userData
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showOverflowMenu) return;

    const closeMenu = () => setShowOverflowMenu(false);
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !overflowButtonRef.current?.contains(target) &&
        !overflowMenuRef.current?.contains(target)
      ) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [showOverflowMenu]);

  const formatBrazilianDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  };

  const handleShowInfo = () => {
    setShowOverflowMenu(false);
    setShowInfoModal(true);
  };

  const handleCheckInvoice = async () => {
    if (window.confirm('Deseja encaminhar o boletim para o faturamento? \n Por favor verifique se o boletim é de expansão ou manutenção, e se os códigos estão de acordo com o mesmo.')) {
      try {
        await updateInvoice(selectedTeam, invoice.key, {
          checked: true,
          checked_closed: 'true_false',
          checked_at: Date.now(),
          checked_by: userData?.name || 'Unknown'
        });
        onRefresh();
      } catch (error) {
        console.error('Error checking invoice:', error);
        alert('Erro ao marcar boletim como corrigido.');
      }
    }
  };

  const handleReturnToSupervision = async () => {
    const email = window.prompt('Digite o email para notificação:');
    if (email && window.confirm('Deseja retornar o boletim para a supervisão?')) {
      try {
        await updateInvoice(selectedTeam, invoice.key, {
          attached: false,
          viewedByFat: false,
          paid: false,
          checked: false,
          checked_closed: 'false_false'
        });
        onRefresh();
      } catch (error) {
        console.error('Error returning invoice:', error);
        alert('Erro ao retornar boletim para supervisão.');
      }
    }
  };

  const handleMarkAsViewed = async () => {
    if (window.confirm('Deseja marcar o boletim como visto?')) {
      try {
        await updateInvoice(selectedTeam, invoice.key, {
          viewedByFat: true
        });
        onRefresh();
      } catch (error) {
        console.error('Error marking as viewed:', error);
        alert('Erro ao marcar como visto.');
      }
    }
  };

  const handleMarkAsPaid = async () => {
    if (window.confirm('Deseja marcar o boletim como pago?')) {
      try {
        await updateInvoice(selectedTeam, invoice.key, {
          paid: true
        });
        onRefresh();
      } catch (error) {
        console.error('Error marking as paid:', error);
        alert('Erro ao marcar como pago.');
      }
    }
  };

  const handleMarkAsAttached = async () => {
    if (window.confirm('Deseja marcar o boletim como anexado?')) {
      try {
        await updateInvoice(selectedTeam, invoice.key, {
          archived: true
        });
        onRefresh();
      } catch (error) {
        console.error('Error marking as attached/archived:', error);
        alert('Erro ao marcar como anexado.');
      }
    }
  };

  const handleSplitInvoice = async () => {
    const confirmMessage = `Deseja dividir o boletim ${invoice.id}?\n\nIsso criará dois novos boletins:\n• ${invoice.id}I (Investimento: NDE, II, RD)\n• ${invoice.id}C (Custeio: NDM, C)\n\nO boletim original será removido.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await splitInvoice(selectedTeam, invoice.key, invoice);
        onRefresh();
        alert('Boletim dividido com sucesso!');
      } catch (error) {
        console.error('Error splitting invoice:', error);
        alert('Erro ao dividir boletim: ' + (error as Error).message);
      }
    }
  };


  const requiresPasswordForEdit = () => {
    return invoice.checked && userRole !== 'admin';
  };

  const handleEditWithPassword = () => {
    if (requiresPasswordForEdit()) {
      const password = window.prompt('Você não tem permissão para editar boletins corrigidos.\nInsira a senha para editar:');
      if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
        onEdit();
      } else if (password !== null) {
        alert('Senha incorreta.');
      }
    } else {
      onEdit();
    }
  };

  const handleDeleteWithPassword = () => {
    if (userRole === 'team' || userRole === 'func') {
      const password = window.prompt('Você não tem permissão para deletar boletins.\nInsira a senha:');
      if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
        onDelete();
      } else if (password !== null) {
        alert('Senha incorreta.');
      }
    } else {
      onDelete();
    }
  };

  const overflowActions: Array<{
    key: string;
    label: string;
    icon: string;
    className: string;
    onClick: () => void | Promise<void>;
  }> = [];

  if (userRole === 'superv' && !invoice.checked) {
    overflowActions.push({
      key: 'check',
      label: 'Encaminhar para Faturamento',
      icon: 'fas fa-arrow-circle-right',
      className: 'check',
      onClick: handleCheckInvoice,
    });
  }

  if (
    (userRole === 'admin' || userRole === 'superv') &&
    hasMixedServiceTypes(invoice.services)
  ) {
    overflowActions.push({
      key: 'split',
      label: 'Dividir Boletim (I/C)',
      icon: 'fas fa-cut',
      className: 'split',
      onClick: handleSplitInvoice,
    });
  }

  if (userRole === 'admin') {
    if (invoice.checked && invoice.checked_by && invoice.checked_at) {
      overflowActions.push({
        key: 'info',
        label: 'Informações do Boletim',
        icon: 'fas fa-info',
        className: 'info',
        onClick: handleShowInfo,
      });
    }

    if (invoice.checked) {
      overflowActions.push({
        key: 'return',
        label: 'Retornar para Supervisão',
        icon: 'fas fa-undo',
        className: 'return',
        onClick: handleReturnToSupervision,
      });
    }

    if (!invoice.viewedByFat) {
      overflowActions.push({
        key: 'view',
        label: 'Marcar como Visto',
        icon: 'fas fa-eye',
        className: 'view',
        onClick: handleMarkAsViewed,
      });
    }

    if (!invoice.paid) {
      overflowActions.push({
        key: 'pay',
        label: 'Marcar como Pago',
        icon: 'fas fa-dollar-sign',
        className: 'pay',
        onClick: handleMarkAsPaid,
      });
    }

    if (!invoice.attached && !invoice.archived) {
      overflowActions.push({
        key: 'attach',
        label: 'Marcar como Arquivado',
        icon: 'fas fa-inbox',
        className: 'attach',
        onClick: handleMarkAsAttached,
      });
    }
  }

  const handleOverflowToggle = () => {
    if (overflowActions.length === 0) return;

    if (!showOverflowMenu && overflowButtonRef.current) {
      const rect = overflowButtonRef.current.getBoundingClientRect();
      const menuWidth = 240;
      const estimatedMenuHeight = overflowActions.length * 42 + 16;
      const hasRoomBelow = window.innerHeight - rect.bottom > estimatedMenuHeight;
      setMenuPosition({
        top: hasRoomBelow
          ? rect.bottom + 8
          : Math.max(8, rect.top - estimatedMenuHeight - 8),
        left: Math.min(
          window.innerWidth - menuWidth - 8,
          Math.max(8, rect.right - menuWidth),
        ),
      });
    }

    setShowOverflowMenu((current) => !current);
  };

  const handleOverflowAction = (action: () => void | Promise<void>) => {
    setShowOverflowMenu(false);
    void action();
  };

  return (
    <div className="invoice-actions">
      <div className="action-group primary-actions">
        <button 
          onClick={onViewPDF} 
          className="action-btn pdf"
          title="Visualizar PDF"
        >
          <i className="fas fa-file-pdf"></i>
        </button>
        
        <button 
          onClick={handleEditWithPassword} 
          className="action-btn edit"
          title="Editar"
        >
          <i className="fas fa-edit"></i>
        </button>
        
        <button 
          onClick={handleDeleteWithPassword} 
          className="action-btn delete"
          title="Deletar"
        >
          <i className="fas fa-trash"></i>
        </button>
        <button
          ref={overflowButtonRef}
          onClick={handleOverflowToggle}
          className="action-btn more"
          title="Mais ações"
          aria-label="Mais ações"
          aria-haspopup="menu"
          aria-expanded={showOverflowMenu}
          disabled={overflowActions.length === 0}
        >
          <i className="fas fa-ellipsis-v"></i>
        </button>
      </div>

      {showOverflowMenu &&
        createPortal(
          <div
            ref={overflowMenuRef}
            className="invoice-actions-menu"
            role="menu"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {overflowActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className="invoice-actions-menu-item"
                role="menuitem"
                onClick={() => handleOverflowAction(action.onClick)}
              >
                <span className={`menu-action-icon ${action.className}`}>
                  <i className={action.icon}></i>
                </span>
                <span>{action.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}

      {/* Info Modal */}
      {showInfoModal && (
        <div className="modal-backdrop" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Informações do Boletim</h5>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="info-item">
                <strong>Verificado por:</strong>
                <span>{invoice.checked_by || 'N/A'}</span>
              </div>
              <div className="info-item">
                <strong>Data da verificação:</strong>
                <span>
                  {invoice.checked_at 
                    ? formatBrazilianDate(invoice.checked_at)
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowInfoModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceActions;
